import { computed, onScopeDispose, watch, type ComputedRef } from 'vue'
import { useRoute } from 'vue-router'

import { TOURS } from '@/components/tour/tours'
import { useBrandStore } from '@/stores/brand'
import { useContentStore } from '@/stores/content'
import { useTourStore } from '@/stores/tour'
import { isLoaded } from '@/types/load-state'

/** How long a route must hold still before its tour is offered. */
export const SECTION_SETTLE_MS = 400

const SECTION_TOUR_BY_ROUTE: Readonly<Record<string, string>> = Object.fromEntries(
  Object.values(TOURS)
    .filter((tour) => tour.variant === 'soft' && tour.route !== undefined)
    .map((tour) => [tour.route as string, tour.id]),
)

function useSectionContent(): ComputedRef<Readonly<Record<string, boolean>>> {
  const brand = useBrandStore()
  const content = useContentStore()

  const hasPosts = computed(() => isLoaded(content.posts) && content.posts.value.length > 0)

  const hasBrand = computed(
    () => brand.artifactPresence.hasBrandFacts || brand.artifactPresence.hasStyleGuide,
  )

  return computed(() => ({
    content: hasPosts.value,
    brand: hasBrand.value,
  }))
}

/** Offers a section its tour the first time the user settles on it with something to explain. */
export function useSectionTour(): void {
  const route = useRoute()
  const tour = useTourStore()
  const ready = useSectionContent()

  const offered = new Set<string>()
  let timer: ReturnType<typeof setTimeout> | undefined

  const candidate = computed<string | null>(() => {
    if (!tour.sectionToursUnlocked) return null
    const id = SECTION_TOUR_BY_ROUTE[String(route.name ?? '')]
    if (id === undefined || offered.has(id)) return null
    return ready.value[id] === true ? id : null
  })

  watch(
    candidate,
    (id) => {
      clearTimeout(timer)
      if (id === null) return
      timer = setTimeout(() => {
        if (candidate.value !== id) return
        offered.add(id)
        tour.start(id)
      }, SECTION_SETTLE_MS)
    },
    { immediate: true },
  )

  onScopeDispose(() => clearTimeout(timer))
}
